import React, { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { MdOutlineAcUnit } from "react-icons/md";
import { GiBread } from "react-icons/gi";
import { LuBriefcase } from "react-icons/lu";
import { HiOutlineCube } from "react-icons/hi2";
import { ResultNotifyContainer } from "../styled/selfService";

const STORAGE_ICONS = {
  0: <MdOutlineAcUnit />,
  1: <GiBread />,
  2: <LuBriefcase />,
  etc: <HiOutlineCube />,
};

const STORAGE_GROUP_TEMPLATE = {
  0: { label: "냉장", items: [] },
  1: { label: "상온", items: [] },
  2: { label: "캐리어", items: [] },
  etc: { label: "기타", items: [] },
};

const HIDDEN_STATUSES = new Set(["PICKUP", "CANCELED"]);

const getStorageNo = (item) => {
  return item?.storageId ?? item?.storageNo ?? item?.storageNumber ?? "-";
};

const getItemKey = (item, fallbackIndex = 0) => {
  return item?.id ?? `${item?.reserveId ?? "reserve"}-${getStorageNo(item)}-${fallbackIndex}`;
};

const ResultNotify = ({
  transformValue,
  details = [],
  selectedItems = [],
  setSelectedItems,
  determineStepAction,
}) => {
  const visibleDetails = useMemo(() => {
    if (!Array.isArray(details)) return [];

    return details.filter((item) => {
      const status = item?.reservationStatus;
      return !HIDDEN_STATUSES.has(status);
    });
  }, [details]);

  const groupedStorage = useMemo(() => {
    return visibleDetails.reduce((acc, item) => {
      const type = Number(item?.type);

      if (type >= 0 && type <= 2) {
        acc[type].items.push(item);
      } else {
        acc.etc.items.push(item);
      }

      return acc;
    }, {
      0: { label: "냉장", items: [] },
      1: { label: "상온", items: [] },
      2: { label: "캐리어", items: [] },
      etc: { label: "기타", items: [] },
    });
  }, [visibleDetails]);

  const selectableItems = useMemo(() => {
    return Object.values(groupedStorage).flatMap((group) => group.items);
  }, [groupedStorage]);

  const selectedIdSet = useMemo(() => {
    return new Set(selectedItems.map((item) => item.id));
  }, [selectedItems]);

  const currentSelectableReserveId = useMemo(() => {
    if (selectedItems.length > 0) {
      return selectedItems[0]?.reserveId;
    }

    return selectableItems[0]?.reserveId ?? null;
  }, [selectedItems, selectableItems]);

  const sameReserveSelectableItems = useMemo(() => {
    if (!currentSelectableReserveId) return [];
    return selectableItems.filter((item) => item.reserveId === currentSelectableReserveId);
  }, [selectableItems, currentSelectableReserveId]);

  const isAllSelected = useMemo(() => {
    if (!sameReserveSelectableItems.length) return false;

    return sameReserveSelectableItems.every((item) => selectedIdSet.has(item.id));
  }, [sameReserveSelectableItems, selectedIdSet]);

  const totalSelected = selectedItems.length;

  useEffect(() => {
    if (!visibleDetails.length) {
      if (selectedItems.length) {
        setSelectedItems([]);
      }
      return;
    }

    const visibleIdSet = new Set(visibleDetails.map((item) => item.id));
    const filteredSelected = selectedItems.filter((item) => visibleIdSet.has(item.id));

    if (filteredSelected.length !== selectedItems.length) {
      setSelectedItems(filteredSelected);
    }
  }, [visibleDetails, selectedItems, setSelectedItems]);

  const isSelected = (item) => {
    return selectedIdSet.has(item.id);
  };

  const toggleCheckbox = (item) => {
    const exists = selectedIdSet.has(item.id);

    if (exists) {
      setSelectedItems((prev) => prev.filter((selected) => selected.id !== item.id));
      return;
    }

    if (selectedItems.length > 0 && selectedItems[0]?.reserveId !== item.reserveId) {
      toast.error("같은 예약 건만 함께 선택할 수 있습니다.");
      return;
    }

    setSelectedItems((prev) => [...prev, item]);
  };

  const toggleSelectAll = () => {
    if (!sameReserveSelectableItems.length) return;

    if (isAllSelected) {
      setSelectedItems((prev) =>
        prev.filter(
          (selected) =>
            !sameReserveSelectableItems.some((item) => item.id === selected.id)
        )
      );
      return;
    }

    setSelectedItems(sameReserveSelectableItems);
  };

  return (
    <ResultNotifyContainer
      className="slideContent"
      style={{ transform: `translateX(${transformValue}%)` }}
    >
      {visibleDetails.length > 0 ? (
        <div className="result">
          <div className="info">
            <div className="resultInfo">
              <div className="user">
                <div>
                  <span className="tit">보관함 선택</span>
                  <span className="con">열 보관함을 선택해주세요.</span>
                </div>
                <div>
                  <span className="tit">선택 수량</span>
                  <span className="con">{totalSelected}개 선택됨</span>
                </div>
              </div>

              <button
                type="button"
                className="toggleButton"
                onClick={toggleSelectAll}
                disabled={!sameReserveSelectableItems.length}
              >
                {isAllSelected ? "전체해제" : "전체선택"}
              </button>
            </div>

            <div className="storageInfo">
              {Object.entries(groupedStorage).map(([typeKey, group]) => {
                if (!group.items.length) return null;

                return (
                  <div className="type" key={typeKey}>
                    <p>{group.label}</p>

                    <ul>
                      {group.items.map((item, index) => {
                        const checked = isSelected(item);
                        const storageNo = getStorageNo(item);

                        return (
                          <li
                            key={getItemKey(item, index)}
                            className={`storageRow ${checked ? "active" : ""}`}
                            onClick={() => toggleCheckbox(item)}
                          >
                            <div className="storageCard">
                              <div className="storageMain">
                                <div className="iconBox">
                                  {STORAGE_ICONS[typeKey] ?? STORAGE_ICONS.etc}
                                </div>

                                <div className="storageText">
                                  <span className="label">보관함 번호</span>
                                  <strong className="number">{storageNo}번</strong>
                                </div>
                              </div>

                              <label
                                className="customInput"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  readOnly
                                  onClick={() => toggleCheckbox(item)}
                                />
                                <span className="checkmark" />
                              </label>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>

            <div className="feeInfo">
              <span>열 보관함을 선택한 뒤 아래 확인 버튼을 눌러주세요.</span>
              <strong>찾기 완료 시 보관이 종료됩니다.</strong>
            </div>
          </div>

          <div className="buttons">
            <button type="button" onClick={() => setSelectedItems([])}>
              선택초기화
            </button>
            <button
              type="button"
              onClick={() => determineStepAction(true)}
              disabled={selectedItems.length === 0}
            >
              확인
            </button>
          </div>
        </div>
      ) : (
        <div className="notDetails">열 수 있는 보관함 정보가 없습니다.</div>
      )}
    </ResultNotifyContainer>
  );
};

export default ResultNotify;
